
import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ClipboardList, ChevronDown, ChevronUp, User, ShoppingCart, AlertCircle, Check, ArrowLeft, CreditCard } from "lucide-react";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function DisputePreviewSection({ 
  isPreviewMode,
  selectedCardholder,
  selectedCard,
  selectedTransactions,
  disputeData,
  disputeReason,
  onBack,
  onSubmit,
  isSubmitting
}) {
  const [expanded, setExpanded] = React.useState(true);
  
  if (!isPreviewMode) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  const getResponseValue = (questionId) => {
    return disputeData?.responses?.[questionId] || "Not answered";
  };
  
  // Helper function to get questions for the current dispute type
  const getQuestions = () => {
    // Return a simplified version for preview
    const type = disputeData?.disputeType;
    
    switch (type) {
      case "Unauthorized transaction – Card in possession":
        return [
          { id: "prev_purchase", text: "Have you ever purchased something from this merchant before?" },
          { id: "card_possession", text: "Are you always in possession of your card?" },
          { id: "authorized_others", text: "Did you authorize anyone to use your card?" },
          { id: "authorized_who", text: "Who did you authorize?" },
          { id: "know_who_made_transaction", text: "Do you know who made this transaction?" },
          { id: "transaction_who", text: "Who made the transaction?" },
          { id: "card_storage", text: "Where is the card usually kept?" },
          { id: "card_storage_other", text: "Where else is the card kept?" },
          { id: "recurring_transaction", text: "Is it possible this is a planned, recurring transaction?" },
          { id: "additional_details", text: "Additional details" }
        ];
        
      case "Lost card":
        return [
          { id: "lost_date", text: "When was the card lost?" },
          { id: "lost_location", text: "Where was the card lost?" },
          { id: "lost_how", text: "How was the card lost?" },
          { id: "anything_else_lost", text: "Was anything else lost?" },
          { id: "authorized_others", text: "Have you authorized anyone else to use your card?" },
          { id: "authorized_who", text: "Who did you authorize?" },
          { id: "additional_details", text: "Additional details" }
        ];

      case "Stolen card":
        return [
          { id: "stolen_date", text: "When was the card stolen?" },
          { id: "stolen_location", text: "Where was the card stolen?" },
          { id: "stolen_how", text: "How was the card stolen?" },
          { id: "anything_else_stolen", text: "Was anything else stolen?" },
          { id: "authorized_others", text: "Have you authorized anyone else to use your card?" },
          { id: "authorized_who", text: "Who did you authorize?" },
          { id: "additional_details", text: "Additional details" }
        ];
        
      case "Merchandise not received":
        return [
          { id: "what_not_received", text: "What was not received?" },
          { id: "expected_delivery_date", text: "When were you supposed to receive the merchandise?" },
          { id: "evidence_of_delivery_date", text: "Do you have evidence of the expected delivery date?" },
          { id: "delivery_date_evidence", text: "Evidence of expected delivery date" },
          { id: "delivery_issue", text: "Was the merchandise delivered late or to wrong location?" },
          { id: "canceled_merchandise", text: "Did you cancel the merchandise?" },
          { id: "cancel_date", text: "When did you cancel?" },
          { id: "cancellation_policy_provided", text: "Was a cancellation policy provided?" },
          { id: "cancel_reason", text: "Why did you cancel?" },
          { id: "merchant_cancel", text: "Did the merchant cancel the merchandise?" },
          { id: "merchant_cancel_date", text: "When did the merchant cancel the merchandise?" },
          { id: "resolve_with_merchant", text: "Did you try to resolve this with the merchant?" },
          { id: "merchant_resolution_evidence", text: "Evidence of communication with merchant" },
          { id: "order_confirmation", text: "Order confirmation / receipt" }
        ];
        
      case "Cancelled recurring transaction":
        return [
          { id: "cancel_date", text: "When did you cancel your subscription?" },
          { id: "cancel_method", text: "How did you request cancellation?" },
          { id: "written_confirmation", text: "Do you have a written confirmation of cancellation?" },
          { id: "cancellation_confirmation", text: "Confirmation of cancellation" },
          { id: "continued_use", text: "After cancellation, did you continue using services?" },
          { id: "cancellation_policy", text: "Merchant's cancellation policy" },
          { id: "additional_info", text: "Additional information" }
        ];
        
      case "Incorrect amount billed":
        return [
          { id: "correct_amount", text: "What was the correct amount you agreed to be charged?" },
          { id: "fee_explanation", text: "Was there a tip, fee, or tax that might explain the difference?" },
          { id: "merchant_contact", text: "Did you contact the merchant to resolve?" },
          { id: "merchant_response", text: "What was the merchant's response?" },
          { id: "merchant_proof", text: "Proof of communication" },
          { id: "original_receipt", text: "Original order confirmation or receipt" },
          { id: "additional_details", text: "Additional details" }
        ];

      case "Card not received":
        return [
          { id: "expected_receive_date", text: "When did you expect to receive your card?" },
          { id: "mail_access_others", text: "Does anyone else have access to your mail?" },
          { id: "mail_access_who", text: "Who has access to your mail?" },
          { id: "card_intercepted", text: "Could anyone have intercepted the card?" },
          { id: "intercepted_by_who", text: "Who could have intercepted the card?" },
          { id: "anything_else_not_received", text: "Was anything else not received?" },
          { id: "what_else_not_received", text: "What else was not received?" },
          { id: "authorized_others_card_not_received", text: "Have you authorized anyone else to use your card (once received)?" },
          { id: "authorized_who_card_not_received", text: "Who did you authorize?" },
          { id: "additional_details_card_not_received", text: "Additional details" }
        ];
      
      case "Duplicate transaction":
        return [
          { id: "recognize_merchant", text: "Do you recognize the merchant name on the transactions?" },
          { id: "merchant_details", text: "Details about this merchant" },
          { id: "multiple_purchases", text: "Did you make multiple purchases from this merchant recently?" },
          { id: "purchase_details", text: "Details about those purchases" },
          { id: "subscription_related", text: "Are any of these charges related to a subscription or recurring payment?" },
          { id: "split_payment", text: "Was this a split payment or installment?" },
          { id: "merchant_informed", text: "Did the merchant inform you of this?" },
          { id: "refund_promised", text: "Did the merchant promise to issue a refund?" },
          { id: "refund_proof", text: "Proof of refund or merchant communication" },
          { id: "receipt_exists", text: "Do you have a receipt for the transaction?" },
          { id: "receipt_upload", text: "Transaction receipt" },
          { id: "additional_details", text: "Additional details" }
        ];
      
      case "Merchant credit not issued":
        return [
          { id: "what_paid_for", text: "What did you pay for?" },
          { id: "why_cancel_return", text: "Why did you cancel the service/returned the merchandise?" },
          { id: "return_date", text: "When did you return the merchandise or cancel the service?" },
          { id: "return_method", text: "How did you return the merchandise or cancel the service?" },
          { id: "return_policy", text: "Was a return policy provided by the merchant?" },
          { id: "policy_upload", text: "Return policy" },
          { id: "provided_credit_evidence_type", text: "Were you provided a credit voucher, voided transaction receipt or refund acknowledgement?" },
          { id: "provided_credit_evidence_date", text: "When were you provided those?" },
          { id: "upload_provided_credit_evidence", text: "Evidence you were provided" },
          { id: "return_confirmation", text: "Return confirmation, email or receipt" },
          { id: "additional_details", text: "Additional details" }
        ];
        
      case "Dissatisfaction with product or service":
        return [
          { id: "received_date", text: "When did you receive the service or merchandise?" },
          { id: "what_is_wrong", text: "What is wrong with the service or merchandise?" },
          { id: "description_issue", text: "Description of the issue" },
          { id: "disputing_full_amount", text: "Are you disputing the full amount?" },
          { id: "disputed_amount_partial", text: "Amount being disputed" },
          { id: "attempted_resolution", text: "Did you try to resolve the issue with the merchant?" },
          { id: "contact_method", text: "How did you contact the merchant?" },
          { id: "contact_date", text: "When did you contact the merchant?" },
          { id: "merchant_response", text: "What was the merchant's response?" },
          { id: "send_evidence", text: "Communication or evidence uploaded" },
          { id: "additional_details", text: "Additional details" }
        ];
        
      default:
        return [];
    }
  };
  
  const formatResponse = (questionId, value) => {
    // Handles undefined, null, or genuinely empty string for optional questions left blank
    if (value === undefined || value === null || value === "") {
      return "Cardholder chose not to answer";
    }

    // If it's an explicit skip message already set by DisputeQuestionsSection, or a file upload confirmation
    if (value === "Cardholder chose not to answer" || 
        value === "Cardholder chose to send file via email" ||
        (typeof value === 'string' && value.startsWith("File uploaded:"))) {
      return value;
    }
    
    // Attempt to format as date if it looks like one (yyyy-MM-dd)
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      try {
        // Parse assuming the date string is in local time or UTC, add T00:00:00 to avoid timezone shifts affecting the date part
        const date = new Date(value + "T00:00:00Z"); // Treat as UTC to be safe with date-only strings
        if (!isNaN(date.getTime())) {
          return format(date, "MMMM d, yyyy");
        }
      } catch (e) {
        // Not a valid date, fall through
      }
    }
    
    // Otherwise, return the value as is (e.g., for text inputs)
    return value;
  };

  return (
    <Card className="mb-6 border-l-4 border-l-indigo-500 shadow-md">
      <CardHeader className="flex flex-row items-center justify-between py-4">
        <CardTitle className="text-lg font-semibold flex items-center">
          <ClipboardList className="mr-2 h-5 w-5 text-indigo-500" />
          Dispute Submission Preview
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="bg-indigo-50 p-4 rounded-md">
            <h3 className="text-base font-medium mb-2 flex items-center">
              <User className="mr-2 h-4 w-4 text-indigo-500" />
              Cardholder Information
            </h3>
            <div className="grid grid-cols-2 gap-x-12 gap-y-2">
              <div>
                <div className="text-sm text-gray-500">Full Name</div>
                <div className="font-medium">{selectedCardholder?.full_name}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Email</div>
                <div className="font-medium">{selectedCardholder?.email}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Phone</div>
                <div className="font-medium">{selectedCardholder?.phone}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Account ID</div>
                <div className="font-medium">{selectedCardholder?.account_id}</div>
              </div>
            </div>
          </div>
          
          <div className="bg-orange-50 p-4 rounded-md mt-4">
            <h3 className="text-base font-medium mb-2 flex items-center">
              <CreditCard className="mr-2 h-4 w-4 text-orange-500" />
              Card Information
            </h3>
            <div className="grid grid-cols-2 gap-x-12 gap-y-2">
              <div>
                <div className="text-sm text-gray-500">Card Number</div>
                <div className="font-medium">Ending in {selectedCard?.id}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Card Type</div>
                <div className="font-medium">{selectedCard?.type}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Status</div>
                <div className="font-medium">{selectedCard?.status}</div>
              </div>
            </div>
          </div>
          
          <div className="bg-red-50 p-4 rounded-md">
            <h3 className="text-base font-medium mb-2 flex items-center">
              <AlertCircle className="mr-2 h-4 w-4 text-red-500" />
              Dispute Reason
            </h3>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2">
              <div>
                <div className="text-sm text-gray-500">Dispute Category</div>
                <div className="font-medium">{disputeReason?.category}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Dispute Type</div>
                <div className="font-medium">{disputeReason?.type}</div>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-md">
            <h3 className="text-base font-medium mb-2 flex items-center">
              <ShoppingCart className="mr-2 h-4 w-4 text-purple-500" />
              Selected Transactions ({selectedTransactions.length})
            </h3>
            <div className="max-h-[200px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-purple-100/50">
                    <TableHead>Merchant</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell className="font-medium">{transaction.merchant}</TableCell>
                      <TableCell>{formatCurrency(transaction.amount)}</TableCell>
                      <TableCell>{format(new Date(transaction.date), "MMM d, yyyy")}</TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={transaction.settlement_status === "Pending" 
                            ? "bg-yellow-50 text-yellow-800 border-yellow-200" 
                            : "bg-green-50 text-green-800 border-green-200"
                          }
                        >
                          {transaction.settlement_status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-md">
            <h3 className="text-base font-medium mb-3 flex items-center">
              <AlertCircle className="mr-2 h-4 w-4 text-green-500" />
              Dispute Questions
            </h3>
            <div className="space-y-4">
              <div className="mt-4">
                <h4 className="text-sm font-medium mb-2">Investigation Questions</h4>
                <div className="space-y-2 border rounded-md p-4 bg-white">
                  {getQuestions().map((question) => {
                    const response = disputeData?.responses?.[question.id];
                    // Always render the question row. formatResponse will handle display.
                    return (
                      <div key={question.id} className="grid grid-cols-2 gap-4 py-2 border-b last:border-b-0">
                        <div className="text-sm font-medium">{question.text}</div>
                        <div className="text-sm">
                          {formatResponse(question.id, response)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="flex justify-between pt-2 pb-6">
        <Button 
          variant="outline"
          onClick={onBack}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Editing
        </Button>
        
        <Button 
          onClick={onSubmit}
          disabled={isSubmitting}
          className="bg-indigo-600 hover:bg-indigo-700 flex items-center gap-2"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-opacity-50 border-t-transparent rounded-full" />
              Submitting...
            </>
          ) : (
            <>
              <Check className="h-4 w-4" />
              Submit Dispute
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
